import {
  InlineDatumReactComponentProps,
  useAnalytics,
  useInlineDataEvents,
} from '@stripe-internal/inline-data';
import {ChatButtonOptions} from '@stripe-internal/conversations-ui';
import {useState, useRef, useEffect} from 'react';
import {v4 as uuid} from 'uuid';
import {view} from '@sail/ui';
import SendUsAnEmailSection, {
  EmailForm,
  FollowUpEmailClientErrors,
} from '../helpers/SendUsAnEmailSection';

export type FollowUpEmailOption = {
  id: string;
  message: string;
};

export type ValueProps = {
  show_contact_support_options?: boolean;
  options_type?: string;
  options?: FollowUpEmailOption[];
  email_form?: EmailForm;
  error_code?: FollowUpEmailClientErrors;
  is_email_form_submitted?: boolean;
  show_cc_emails_field?: boolean;
  existing_cc_emails?: string[];
};

export const MerchantFollowUpCaseOptions = (
  props: InlineDatumReactComponentProps,
) => {
  const {datum, onDone, action, isDone, actionError, outcome} = props;
  const value = datum.value as ValueProps;
  const events = useInlineDataEvents();

  const {
    show_contact_support_options: showContactSupportOptions,
    options_type: optionsType,
    options,
    email_form: emailForm,
    error_code: errorCode,
    is_email_form_submitted: isEmailFormSubmitted,
    show_cc_emails_field: showCcEmailsField,
    existing_cc_emails: existingCcEmails,
  } = value;

  const [selectedOption, setSelectedOption] = useState<
    FollowUpEmailOption | undefined
  >(() =>
    outcome
      ? (options || []).find((option) => option.id === outcome)
      : undefined,
  );

  useEffect(() => {
    if (!outcome) {
      return;
    }
    setSelectedOption(
      (prev) => prev || (options || []).find((option) => option.id === outcome),
    );
  }, [outcome, options]);

  useEffect(() => {
    if (
      selectedOption?.id !== 'channel_email' ||
      !isEmailFormSubmitted ||
      isDone
    ) {
      return;
    }

    onDone({
      type: 'success',
      message: selectedOption?.message,
      extra: {
        outcome: selectedOption?.id,
        value,
      },
    });
  }, [isDone, isEmailFormSubmitted, onDone, selectedOption, value]);

  const nonce = useRef<string>(uuid()).current;
  const analytics = useAnalytics();

  const onConvOptionPress = (option: FollowUpEmailOption) => {
    events.emit({name: 'inline-data-trigger-scroll-to-top'});
    setSelectedOption(option);
    analytics.action('uh_merchant_follow_up_case_option_selected', {
      followUpCaseOption: option.id,
    });
    if (option.id !== 'channel_email') {
      onDone({
        type: 'success',
        message: option.message,
        extra: {
          outcome: option.id,
        },
      });
    } else {
      events.emitWithResult({
        name: 'inline-data-action',
        data: {
          purpose: 'channel_selection',
          channelID: option.id,
        },
        nonce,
      });
    }
  };

  const convOptions = (options || []).map((option: FollowUpEmailOption) => {
    return {
      value: option.id,
      label: option.message,
      onPress: () => onConvOptionPress(option),
    };
  });

  const handleCancelForm = () => {
    analytics.action('uh_merchant_follow_up_case_cancel_form');
    setSelectedOption(undefined);
  };

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <ChatButtonOptions
        data-testid="chat-options"
        options={convOptions}
        selectedOptionId={selectedOption?.id}
        animated
      />
      <SendUsAnEmailSection
        action={action}
        nonce={nonce}
        handleCancelForm={handleCancelForm}
        emailForm={emailForm}
        isSectionVisible={selectedOption?.id === 'channel_email'}
        isEmailFormSubmitted={isDone || !!isEmailFormSubmitted}
        showUnauthenticatedEmailField={false}
        showCcEmailsField={showCcEmailsField}
        existingCcEmails={existingCcEmails}
        showContactSupportOptions={showContactSupportOptions}
        clientError={
          errorCode ? (errorCode as FollowUpEmailClientErrors) : undefined
        }
        serverError={actionError}
        optionsType={optionsType}
      />
    </view.div>
  );
};
